import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, CATEGORY, errMsg, fmtDate, PRIORITY, STATUS } from "@/lib/api";
import { EmptyState, PageHeader, PriorityBadge, Spinner, StatusBadge } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Pencil, Search, Plus, Ticket, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function TicketsPage() {
  const [tickets, setTickets] = useState(null);
  const [filters, setFilters] = useState({ status: "", priority: "", category: "", q: "" });
  const [editTicket, setEditTicket] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/tickets", { params }).then(({ data }) => setTickets(data)).catch(() => setTickets([]));
  };
  useEffect(() => { load(); }, [filters]);

  const submitEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/tickets/${editTicket.id}`, {
        title: editTicket.title, description: editTicket.description,
        category: editTicket.category, priority: editTicket.priority,
      });
      toast.success("Chamado atualizado");
      setEditTicket(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteTicket = async () => {
    setSaving(true);
    try {
      await api.delete(`/tickets/${deleteTarget.id}`);
      toast.success("Chamado excluído");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="tickets-page">
      <PageHeader title="Chamados" subtitle="Todos os chamados dos seus clientes">
        <Button data-testid="staff-new-ticket-button" onClick={() => navigate("/app/novo")}
          className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
          <Plus className="h-4 w-4 mr-1.5" /> Novo Chamado
        </Button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative flex-1 min-w-52">
          <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="tickets-search-input" placeholder="Buscar por título ou descrição…"
            value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="pl-9 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
        </div>
        {[
          { key: "status", label: "Status", map: STATUS, testid: "filter-status" },
          { key: "priority", label: "Prioridade", map: PRIORITY, testid: "filter-priority" },
          { key: "category", label: "Categoria", map: CATEGORY, testid: "filter-category" },
        ].map(({ key, label, map, testid }) => (
          <Select key={key} value={filters[key] || "all"} onValueChange={(v) => setFilters({ ...filters, [key]: v === "all" ? "" : v })}>
            <SelectTrigger data-testid={testid} className="w-44 bg-zinc-900 border-zinc-800 text-zinc-200 rounded-md">
              <SelectValue placeholder={label} />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
              <SelectItem value="all">Todos · {label}</SelectItem>
              {Object.entries(map).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        ))}
        {(filters.status || filters.priority || filters.category || filters.q) && (
          <Button data-testid="clear-filters-button" variant="ghost"
            onClick={() => setFilters({ status: "", priority: "", category: "", q: "" })}
            className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
            Limpar filtros
          </Button>
        )}
      </div>

      {!tickets ? <Spinner /> : tickets.length === 0 ? (
        <EmptyState icon={Ticket} title="Nenhum chamado encontrado" subtitle="Ajuste os filtros ou aguarde novos chamados dos clientes." />
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto">
          <table className="w-full text-sm min-w-[960px]">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                <th className="px-4 py-3 text-xs font-medium text-zinc-500">Nº</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500">Título</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500">Cliente</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500">Categoria</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500">Local</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500">Prioridade</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500">Status</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500">Origem</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500">Atualizado</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id} data-testid={`ticket-row-${t.id}`} onClick={() => navigate(`/app/chamados/${t.id}`)}
                  className="border-b border-zinc-800/60 hover:bg-zinc-800/50 cursor-pointer transition-colors duration-200">
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">#{String(t.number).padStart(4, "0")}</td>
                  <td className="px-4 py-3 text-zinc-100 font-medium max-w-56 truncate">{t.title}</td>
                  <td className="px-4 py-3 text-zinc-400">{t.client_name}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{CATEGORY[t.category]}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs" data-testid={`ticket-area-cell-${t.id}`}>{t.area_name || "—"}</td>
                  <td className="px-4 py-3"><PriorityBadge value={t.priority} /></td>
                  <td className="px-4 py-3"><StatusBadge value={t.status} /></td>
                  <td className="px-4 py-3 text-xs text-zinc-500">{t.source === "api" ? "API" : "Portal"}</td>
                  <td className="px-4 py-3 text-xs text-zinc-500">{fmtDate(t.updated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-1">
                      <Button data-testid={`ticket-edit-${t.id}`} variant="ghost" size="icon" title="Alterar chamado"
                        onClick={(e) => { e.stopPropagation(); setEditTicket({ id: t.id, number: t.number, title: t.title, description: t.description, category: t.category, priority: t.priority }); }}
                        className="h-8 w-8 text-zinc-500 hover:text-indigo-400 hover:bg-zinc-800">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {user?.role === "tenant_admin" && (
                        <Button data-testid={`ticket-delete-${t.id}`} variant="ghost" size="icon" title="Excluir chamado"
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(t); }}
                          className="h-8 w-8 text-zinc-500 hover:text-rose-400 hover:bg-zinc-800">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editTicket} onOpenChange={(v) => !v && setEditTicket(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Alterar chamado #{String(editTicket?.number || 0).padStart(4, "0")}</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">Edite título, descrição, categoria e prioridade.</DialogDescription>
          </DialogHeader>
          {editTicket && (
            <form onSubmit={submitEdit} className="space-y-4" data-testid="ticket-edit-form">
              <div>
                <Label className="text-xs text-zinc-400">Título</Label>
                <Input data-testid="edit-ticket-title-input" required maxLength={120} value={editTicket.title}
                  onChange={(e) => setEditTicket({ ...editTicket, title: e.target.value })}
                  className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-zinc-400">Categoria</Label>
                  <Select value={editTicket.category} onValueChange={(v) => setEditTicket({ ...editTicket, category: v })}>
                    <SelectTrigger data-testid="edit-ticket-category-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-200 rounded-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
                      {Object.entries(CATEGORY).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Prioridade</Label>
                  <Select value={editTicket.priority} onValueChange={(v) => setEditTicket({ ...editTicket, priority: v })}>
                    <SelectTrigger data-testid="edit-ticket-priority-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-200 rounded-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
                      {Object.entries(PRIORITY).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Descrição</Label>
                <Textarea data-testid="edit-ticket-description-input" required rows={4} value={editTicket.description}
                  onChange={(e) => setEditTicket({ ...editTicket, description: e.target.value })}
                  className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
              </div>
              <Button data-testid="edit-ticket-submit" type="submit" disabled={saving}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar alterações"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Excluir chamado</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Tem certeza que deseja excluir o chamado #{String(deleteTarget?.number || 0).padStart(4, "0")} — <span className="text-zinc-200 font-medium">{deleteTarget?.title}</span>? O histórico e as mensagens também serão removidos. Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2" data-testid="delete-ticket-dialog">
            <Button data-testid="delete-ticket-cancel" variant="ghost" onClick={() => setDeleteTarget(null)}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
              Cancelar
            </Button>
            <Button data-testid="delete-ticket-confirm" onClick={deleteTicket} disabled={saving}
              className="bg-rose-600 hover:bg-rose-500 text-white rounded-md transition-colors duration-200">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Excluir"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
